// src/components/PublicBusinessClubConfirm.tsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

/**
 * PUBLIC BusinessClub double opt-in confirmation page (audit M-8).
 *
 * The opt-in email links here:
 *   /businessclub/confirm/<tenantId>?memberId=...#token=...
 *
 * Flow (mirrors PublicOfferConfirmation's fragment-token pattern):
 *   1. The token arrives in the URL FRAGMENT (#token=...) — fragments are
 *      never sent to servers and never appear in logs/referrers. Captured
 *      once, then stripped from the URL before router effects rewrite it.
 *   2. GET /api/business-club?action=confirm-email&memberId=...&token=...
 *      (token sent as a query param ONCE — it is single-use and cleared
 *      server-side after verification).
 *   3. Server verifies, sets consent, sends the welcome email (promo code).
 *
 * Security notes:
 *   - The memberId is a UUID, the token is 32 random bytes: guessing or
 *     brute-forcing either is infeasible (audit M-2).
 *   - No PII is displayed — only a neutral confirmation state.
 */
export default function PublicBusinessClubConfirm({ tenantId }: { tenantId?: string }) {
  const { t } = useTranslation("common");
  const [state, setState] = useState<"loading" | "confirmed" | "already" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    // Capture the fragment token once, then clean the URL (same pattern as
    // PublicOfferConfirmation — prevents token leakage via referrer/history).
    const hash = window.location.hash || "";
    const tokenMatch = hash.match(/token=([A-Za-z0-9]+)/);
    const token = tokenMatch ? tokenMatch[1] : "";
    const memberId = new URLSearchParams(window.location.search).get("memberId") || "";

    if (window.history?.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + "?memberId=" + encodeURIComponent(memberId));
    }

    async function run() {
      if (!token || !memberId) {
        if (!cancelled) {
          setState("error");
          setErrorMsg(t("businessClub.confirmMissingParams"));
        }
        return;
      }
      try {
        const res = await fetch(
          `/api/business-club?action=confirm-email&memberId=${encodeURIComponent(memberId)}&token=${encodeURIComponent(token)}`,
          { method: "GET" }
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setState("error");
          setErrorMsg(data?.error || t("businessClub.confirmFailed"));
          return;
        }
        setState(data.alreadyConfirmed ? "already" : "confirmed");
      } catch {
        if (!cancelled) {
          setState("error");
          setErrorMsg(t("businessClub.confirmFailed"));
        }
      }
    }
    run();

    return () => { cancelled = true; };
  }, [t]);

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4 md:p-6 font-sans">
      <div className="relative w-full max-w-md">
        {state === "loading" && (
          <div className="bg-surface border border-border rounded-[2.5rem] shadow-2xl p-12 text-center">
            <Loader2 className="animate-spin mx-auto text-accent" size={40} />
            <h1 className="mt-6 font-black text-xl text-fg">{t("businessClub.confirmLoading")}</h1>
          </div>
        )}

        {(state === "confirmed" || state === "already") && (
          <div className="bg-surface border border-border rounded-[2.5rem] shadow-2xl p-12 text-center">
            <div className="flex justify-center mb-5 text-emerald-500">
              <CheckCircle2 size={52} />
            </div>
            <h1 className="font-black text-2xl text-fg mb-3">
              {t("businessClub.confirmSuccessTitle")}
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed">
              {state === "already"
                ? t("businessClub.confirmAlready")
                : t("businessClub.confirmSuccessBody")}
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="bg-surface border border-border rounded-[2.5rem] shadow-2xl p-12 text-center">
            <div className="flex justify-center mb-5 text-red-500">
              <XCircle size={52} />
            </div>
            <h1 className="font-black text-2xl text-fg mb-3">
              {t("businessClub.confirmFailedTitle")}
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed">{errorMsg}</p>
            <p className="text-xs text-slate-400 mt-6">
              {t("businessClub.confirmHint")} {tenantId ? "" : ""}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
